import { DoctoralContactHub } from './components/DoctoralContactHub';
import { DoctoralWorkflowWorkspace } from './components/DoctoralWorkflowView';
import { DoctoralGroupLeaderPage } from './components/DoctoralGroupLeaderPage';
import { DoctoralSharedService } from './components/DoctoralSharedService';
import { pathValue, workflowTitles, type WorkflowOverview } from './workflowApi';
import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  DoctoralSummaryData,
  DoctoralCabinetCard,
  DoctoralProcessOverview,
  DoctoralProfileData,
  DoctoralTopicData,
  DoctoralPlanData,
  DoctoralNirdOverview,
  DoctoralPracticeData,
  DoctoralInternshipData,
  DoctoralPublication,
  DoctoralAchievement,
  DoctoralDefenseData,
  DoctoralCompletionData,
  DoctoralEnuSourcesData,
  DoctoralTrajectoryData,
  CollaborationItem,
  AuditLogItem,
} from './types';
import { doctoralApi } from '@/features/workspace/api';
import { DoctoralCabinetGrid } from './components/DoctoralCabinetGrid';
import { DoctoralWorkspaceOverlay, DoctoralViewKey } from './components/DoctoralWorkspaceOverlay';
import { DoctoralTrajectoryOverlay } from './components/DoctoralTrajectoryOverlay';
import { DoctoralJointScienceOverlay } from './components/DoctoralJointScienceOverlay';
import {
  DoctoralSupportOverlay,
  type DoctoralSupportKind,
} from './components/DoctoralSupportOverlay';
import {
  HistoryModal,
  AdvisorSampleModal,
  AddPublicationModal,
  GenericCardModal,
  ProblemReportModal,
} from './components/DoctoralModals';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { AdvisorExchangePage } from '@/features/advisor/components/AdvisorExchangePage';
import { routes } from '@/lib/routes';
import { SuperAiWorkspaceModal } from '@/features/super-ai';
import { useToast } from '@/hooks/useToast';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';

const WORKSPACE_VIEWS = new Set<DoctoralViewKey>([
  'overview',
  'guide',
  'profile',
  'topic',
  'plan',
  'nird',
  'practices',
  'internship',
  'publications',
  'achievements',
  'documents',
  'defense',
  'completion',
  'sources',
]);

function resolveCabinetHome(pathname: string): string {
  if (pathname === routes.learner || pathname.startsWith(`${routes.learner}/`)) {
    return routes.learner;
  }
  return routes.doctoral;
}

function parseWorkspaceView(value: string | undefined): DoctoralViewKey {
  if (value && WORKSPACE_VIEWS.has(value as DoctoralViewKey)) {
    return value as DoctoralViewKey;
  }
  return 'overview';
}

const DoctoralWorkspaceProcessRoute: React.FC<{
  render: (view: DoctoralViewKey) => React.ReactNode;
}> = ({ render }) => {
  const { view } = useParams<{ view: string }>();
  return <>{render(parseWorkspaceView(view))}</>;
};

export const DoctoralPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { notify } = useToast();
  const cabinetHome = useMemo(() => resolveCabinetHome(pathname), [pathname]);
  const backToCabinet = () => navigate(cabinetHome);

  const [summary, setSummary] = useState<DoctoralSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [overviewData, setOverviewData] = useState<DoctoralProcessOverview | null>(null);
  const [profileData, setProfileData] = useState<DoctoralProfileData | null>(null);
  const [topicData, setTopicData] = useState<DoctoralTopicData | null>(null);
  const [planData, setPlanData] = useState<DoctoralPlanData | null>(null);
  const [nirdData, setNirdData] = useState<DoctoralNirdOverview | null>(null);
  const [practicesData, setPracticesData] = useState<DoctoralPracticeData[]>([]);
  const [internshipData, setInternshipData] = useState<DoctoralInternshipData | null>(null);
  const [publicationsData, setPublicationsData] = useState<DoctoralPublication[]>([]);
  const [achievementsData, setAchievementsData] = useState<DoctoralAchievement[]>([]);
  const [defenseData, setDefenseData] = useState<DoctoralDefenseData | null>(null);
  const [completionData, setCompletionData] = useState<DoctoralCompletionData | null>(null);
  const [sourcesData, setSourcesData] = useState<DoctoralEnuSourcesData | null>(null);
  const [trajectoryData, setTrajectoryData] = useState<DoctoralTrajectoryData | null>(null);
  const [collaborationsData, setCollaborationsData] = useState<CollaborationItem[]>([]);
  const [auditLogsData, setAuditLogsData] = useState<AuditLogItem[]>([]);

  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [advisorSampleModalOpen, setAdvisorSampleModalOpen] = useState(false);
  const [addPubModalOpen, setAddPubModalOpen] = useState(false);
  const [superAiModalOpen, setSuperAiModalOpen] = useState(false);
  const [problemReportModalOpen, setProblemReportModalOpen] = useState(false);
  const [genericModalState, setGenericModalState] = useState<{
    open: boolean;
    title: string;
    description: string;
    details?: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  useEffect(() => {
    doctoralApi
      .summary()
      .then((res) => {
        if (res && res.profile) setSummary(res);
      })
      .catch(() => {})
      .finally(() => setSummaryLoading(false));

    doctoralApi
      .overview()
      .then(setOverviewData)
      .catch(() => {});
    doctoralApi
      .profile()
      .then(setProfileData)
      .catch(() => {});
    doctoralApi
      .topic()
      .then(setTopicData)
      .catch(() => {});
    doctoralApi
      .plan()
      .then(setPlanData)
      .catch(() => {});
    doctoralApi
      .nird()
      .then(setNirdData)
      .catch(() => {});
    doctoralApi
      .practices()
      .then(setPracticesData)
      .catch(() => {});
    doctoralApi
      .internship()
      .then(setInternshipData)
      .catch(() => {});
    doctoralApi
      .publications()
      .then(setPublicationsData)
      .catch(() => {});
    doctoralApi
      .achievements()
      .then(setAchievementsData)
      .catch(() => {});
    doctoralApi
      .defense()
      .then(setDefenseData)
      .catch(() => {});
    doctoralApi
      .completion()
      .then(setCompletionData)
      .catch(() => {});
    doctoralApi
      .sources()
      .then(setSourcesData)
      .catch(() => {});
    doctoralApi
      .trajectory()
      .then(setTrajectoryData)
      .catch(() => {});
    doctoralApi
      .collaborations()
      .then(setCollaborationsData)
      .catch(() => {});
    doctoralApi
      .auditLogs()
      .then(setAuditLogsData)
      .catch(() => {});
  }, []);

  const handleOpenCard = (card: DoctoralCabinetCard) => {
    switch (card.route_id) {
      case 'DOCTORAL_PROCESS':
        navigate('process/overview');
        break;
      case 'DOCTORAL_WORKSPACE':
      case 'TP201 / doctoral workspace':
        navigate('plan');
        break;
      case 'SUPERVISOR_RELATION':
        navigate(`${routes.supervisor}/supervision`);
        break;
      case 'PUBLICATION_PLAN':
        navigate('publications');
        break;
      case 'ACADEMIC_CONNECTIONS':
        navigate('connections');
        break;
      case 'FILE_HUB':
        navigate('files');
        break;
      case 'LEARNER_TRAJECTORY':
        navigate('trajectory');
        break;
      case 'JOINT_SCIENCE':
        navigate('joint-science');
        break;
      case 'DUTY':
        navigate('duty');
        break;
      case 'SURVEYS':
        navigate('surveys');
        break;
      case 'RESEARCH_TOOLS':
        navigate('tools');
        break;
      case 'CALENDAR':
        navigate('calendar');
        break;
      case 'ACADEMIC_NOTEBOOK':
        navigate('notebook');
        break;
      case 'GROUP_HEAD':
        navigate('group');
        break;
      case 'MEETINGS':
      case 'U129_MEETING':
        navigate('meetings');
        break;
      default:
        navigate('process/overview');
        break;
    }
  };

  const handleOpenModal = (modalId: string, payload?: unknown) => {
    switch (modalId) {
      case 'history':
        setHistoryModalOpen(true);
        break;
      case 'advisor_sample':
        setAdvisorSampleModalOpen(true);
        break;
      case 'add_pub':
        setAddPubModalOpen(true);
        break;
      case 'super_ai':
        setSuperAiModalOpen(true);
        break;
      case 'problem_report':
        setProblemReportModalOpen(true);
        break;
      case 'meetings':
        navigate('meetings');
        break;
      case 'advisor_contact':
        navigate(`${cabinetHome}/advisor`);
        break;
      case 'create_request':
        navigate(routes.newRequest);
        break;
      case 'generic': {
        const p = payload as { title?: string; desc?: string; details?: string };
        setGenericModalState({
          open: true,
          title: p?.title || 'Информация',
          description: p?.desc || '',
          details: p?.details,
        });
        break;
      }
      default:
        break;
    }
  };

  const handleSaveProfile = async (updated: DoctoralProfileData) => {
    try {
      const res = await doctoralApi.updateProfile(updated);
      setProfileData(res);
    } catch {
      notify('Не удалось сохранить профиль', 'error');
    }
  };

  const handleUploadIup = async (file: File) => {
    try {
      const res = await doctoralApi.updatePlan({
        file_name: file.name,
        word_status: 'Загружен',
        signatures_status: 'На согласовании',
      });
      setPlanData(res);
    } catch {
      notify('Не удалось загрузить ИУП', 'error');
    }
  };

  const handleSendNirdToAdvisor = async (semester: number) => {
    try {
      const res = await doctoralApi.updateNird({
        semester,
        advisor_status: 'На проверке',
      });
      setNirdData(res);
    } catch {
      notify('Не удалось отправить НИРД научному руководителю', 'error');
    }
  };

  const handleUploadNirdReport = async (semester: number, file: File) => {
    try {
      const res = await doctoralApi.updateNird({
        semester,
        report_file: file.name,
      });
      setNirdData(res);
    } catch {
      notify('Не удалось загрузить отчёт НИРД', 'error');
    }
  };

  const handleAddAchievement = async (item: DoctoralAchievement) => {
    try {
      const res = await doctoralApi.createAchievement(item);
      setAchievementsData((prev) => [res, ...prev]);
    } catch {
      notify('Не удалось добавить достижение', 'error');
    }
  };

  const handleAddPublication = async (item: DoctoralPublication) => {
    try {
      const res = await doctoralApi.createPublication(item);
      setPublicationsData((prev) => [res, ...prev]);
    } catch {
      notify('Не удалось добавить публикацию', 'error');
    }
  };

  const handleAddCollaboration = async (item: {
    kind: string;
    title: string;
    description: string;
  }) => {
    try {
      const res = await doctoralApi.createCollaboration(item);
      setCollaborationsData((prev) => [res, ...prev]);
    } catch {
      notify('Не удалось создать совместную инициативу', 'error');
    }
  };

  const handleReportProblem = async (topic: string, message: string) => {
    try {
      return await doctoralApi.reportProblem({ topic, message });
    } catch {
      notify('Не удалось зарегистрировать обращение', 'error');
      return {
        ok: false,
        ticket_id: '',
        message: 'Не удалось зарегистрировать обращение',
      };
    }
  };

  const updateWorkflowOverview = (data: WorkflowOverview) => {
    setOverviewData((old) =>
      old
        ? {
            ...old,
            overall_readiness: data.percent ?? 0,
            next_actions: data.modules
              .filter((m) => m.percent !== 100)
              .slice(0, 4)
              .map((m, i) => ({
                num: String(i + 1),
                title: workflowTitles[m.module] ?? m.module,
                route_key: m.module,
                is_completed: false,
              })),
          }
        : old,
    );
    setProfileData((old) =>
      old
        ? {
            ...old,
            first_name_ru: String(pathValue(data.profile, 'personal.ru.given') || ''),
            last_name_ru: String(pathValue(data.profile, 'personal.ru.family') || ''),
            first_name_kz: String(pathValue(data.profile, 'personal.kz.given') || ''),
            last_name_kz: String(pathValue(data.profile, 'personal.kz.family') || ''),
            main_email: String(pathValue(data.profile, 'account.email') || ''),
            group_name: String(pathValue(data.profile, 'academic.group') || old.group_name || ''),
            program_title: String(
              pathValue(data.profile, 'academic.program') || old.program_title || '',
            ),
          }
        : old,
    );
    setTopicData((old) =>
      old
        ? {
            ...old,
            topic_ru: String(pathValue(data.profile, 'topicFlow.title.ru') || ''),
            supervisor_name: String(pathValue(data.profile, 'topicFlow.domestic.name') || ''),
          }
        : old,
    );
  };

  const renderWorkspace = (initialView: DoctoralViewKey) => (
    <DoctoralWorkspaceOverlay
      initialView={initialView}
      renderSection={(view, onNavigate) => (
        <DoctoralWorkflowWorkspace
          view={view}
          onNavigate={onNavigate}
          onOverview={updateWorkflowOverview}
        />
      )}
      onClose={backToCabinet}
      onOpenModal={handleOpenModal}
      overviewData={overviewData}
      profileData={profileData}
      topicData={topicData}
      planData={planData}
      nirdData={nirdData}
      practicesData={practicesData}
      internshipData={internshipData}
      publicationsData={publicationsData}
      achievementsData={achievementsData}
      defenseData={defenseData}
      completionData={completionData}
      sourcesData={sourcesData}
      onSaveProfile={handleSaveProfile}
      onUploadIup={handleUploadIup}
      onSendNirdToAdvisor={handleSendNirdToAdvisor}
      onUploadNirdReport={handleUploadNirdReport}
      onAddAchievement={handleAddAchievement}
      onOpenAcademyProfile={() => navigate(routes.profile)}
      onOpenService={(href) => navigate(href)}
      serviceLinks={{
        prime: `${cabinetHome}/prime`,
        journals: routes.journals,
        conferences: routes.portalSection('competitions'),
        notebook: `${cabinetHome}/notebook`,
        calendar: `${cabinetHome}/calendar`,
        files: `${cabinetHome}/files`,
        surveys: `${cabinetHome}/surveys`,
        messages: `${cabinetHome}/connections`,
        help: routes.portalSection('instructions'),
        settings: routes.security,
      }}
    />
  );

  const renderSupport = (kind: DoctoralSupportKind) => (
    <DoctoralSupportOverlay
      kind={kind}
      onClose={backToCabinet}
      groupName={summary?.profile.group_name}
      programTitle={summary?.profile.program_title}
      yearSemester={summary?.profile.year_semester}
      onOpenTools={() => navigate(`${cabinetHome}/tools`)}
    />
  );

  const cabinetIndex = (
    <CabinetWorkspaceFrame
      links={{
        notebook: `${cabinetHome}/notebook`,
        calendar: `${cabinetHome}/calendar`,
        meetings: `${cabinetHome}/meetings`,
        connections: `${cabinetHome}/connections`,
        alliances: `${cabinetHome}/connections`,
        qa: routes.portalSection('instructions'),
        support: routes.portalSection('contacts'),
      }}
    >
      <div className="w-full space-y-8">
        {summaryLoading ? (
          <div className="text-center text-slate-400 py-16">Загрузка кабинета…</div>
        ) : !summary ? (
          <div className="text-center text-slate-400 py-16">
            Не удалось загрузить кабинет докторанта
          </div>
        ) : (
          <DoctoralCabinetGrid
            summary={summary}
            onOpenCard={handleOpenCard}
            onOpenModal={handleOpenModal}
          />
        )}
      </div>
      <ScientificGpsBlock openTo={`${cabinetHome}/trajectory`} />
    </CabinetWorkspaceFrame>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 role-cabinet-page">
      <Routes>
        <Route index element={cabinetIndex} />

        <Route
          path="process/:view"
          element={<DoctoralWorkspaceProcessRoute render={renderWorkspace} />}
        />
        <Route path="overview" element={renderWorkspace('overview')} />
        <Route path="guide" element={renderWorkspace('guide')} />
        <Route path="profile" element={renderWorkspace('profile')} />
        <Route path="topic" element={renderWorkspace('topic')} />
        <Route path="plan" element={renderWorkspace('plan')} />
        <Route path="nird" element={renderWorkspace('nird')} />
        <Route path="practices" element={renderWorkspace('practices')} />
        <Route path="internship" element={renderWorkspace('internship')} />
        <Route path="publications" element={renderWorkspace('publications')} />
        <Route path="achievements" element={renderWorkspace('achievements')} />
        <Route path="documents" element={renderWorkspace('documents')} />
        <Route path="defense" element={renderWorkspace('defense')} />
        <Route path="completion" element={renderWorkspace('completion')} />
        <Route path="sources" element={renderWorkspace('sources')} />

        <Route
          path="trajectory"
          element={
            <DoctoralTrajectoryOverlay
              data={trajectoryData}
              onClose={backToCabinet}
              onOpenJointScience={() => navigate(`${cabinetHome}/joint-science`)}
            />
          }
        />
        <Route
          path="joint-science"
          element={
            <DoctoralJointScienceOverlay
              collaborations={collaborationsData}
              onClose={backToCabinet}
              onAddCollaboration={handleAddCollaboration}
            />
          }
        />
        <Route
          path="connections"
          element={
            <RoleAcademicConnectionsOverlay
              isOpen
              onClose={backToCabinet}
              roleContext="DOCTORAL_STUDENT"
              roleTitle="Докторант"
            />
          }
        />
        <Route
          path="tools/*"
          element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
        />
        <Route
          path="files/*"
          element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
        />
        <Route
          path="meetings"
          element={<AdvisorExchangePage view="meetings" onClose={backToCabinet} />}
        />
        <Route
          path="meetings/*"
          element={<AdvisorExchangePage view="meetings" onClose={backToCabinet} />}
        />

        <Route path="duty" element={renderSupport('duty')} />
        <Route
          path="surveys"
          element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
        />
        <Route
          path="calendar"
          element={<DoctoralSharedService kind="calendar" onClose={backToCabinet} />}
        />
        <Route
          path="notebook"
          element={<DoctoralSharedService kind="notebook" onClose={backToCabinet} />}
        />
        <Route
          path="prime"
          element={
            <div className="dp120-root open">
              <main className="dp120-main">
                <button className="dp120-back" onClick={backToCabinet}>
                  ← Назад
                </button>
                <DoctoralWorkflowWorkspace
                  view="prime"
                  onNavigate={(v) => navigate(`${cabinetHome}/process/${v}`)}
                />
              </main>
            </div>
          }
        />
        <Route path="advisor" element={<DoctoralContactHub onClose={backToCabinet} />} />
        <Route
          path="review/:studentId/:module"
          element={<DoctoralReviewRoute onClose={backToCabinet} />}
        />
        <Route path="group" element={<DoctoralGroupLeaderPage onClose={backToCabinet} />} />
      </Routes>

      <HistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        historyLogs={auditLogsData}
      />

      <AdvisorSampleModal
        isOpen={advisorSampleModalOpen}
        onClose={() => setAdvisorSampleModalOpen(false)}
        docTitle="Служебная записка научного консультанта"
      />

      <AddPublicationModal
        isOpen={addPubModalOpen}
        onClose={() => setAddPubModalOpen(false)}
        onAdd={(data) =>
          handleAddPublication({
            title: data.title,
            journal: data.journal,
            doi: data.doi,
            indexing: data.indexing,
            is_defense_ready: true,
          })
        }
      />

      <SuperAiWorkspaceModal
        isOpen={superAiModalOpen}
        onClose={() => setSuperAiModalOpen(false)}
        activeRole="DOCTORAL_STUDENT"
      />

      <ProblemReportModal
        isOpen={problemReportModalOpen}
        onClose={() => setProblemReportModalOpen(false)}
        onSubmit={(data) => handleReportProblem(data.topic, data.message)}
      />

      <GenericCardModal
        isOpen={genericModalState.open}
        onClose={() => setGenericModalState({ open: false, title: '', description: '' })}
        cardNum="00"
        title={genericModalState.title}
        desc={
          genericModalState.description +
          (genericModalState.details ? ' — ' + genericModalState.details : '')
        }
      />
    </div>
  );
};

function DoctoralReviewRoute({ onClose }: { onClose: () => void }) {
  const { studentId, module } = useParams();
  const allowed = [
    'topic',
    'plan',
    'nird',
    'practices',
    'internship',
    'publications',
    'achievements',
    'defense',
    'completion',
  ];
  if (!studentId || !module || !allowed.includes(module)) return <p>Раздел проверки не найден.</p>;
  return (
    <div className="dp120-root open">
      <main className="dp120-main">
        <button className="dp120-back" onClick={onClose}>
          ← Назад
        </button>
        <DoctoralWorkflowWorkspace
          view={module as DoctoralViewKey}
          studentId={studentId}
          onNavigate={() => undefined}
        />
      </main>
    </div>
  );
}
